/**
 * Audio product-class grounding for Ask Cleo / layman explain.
 * Preamps must never be bucketed with power amps — they do not drive speakers.
 */

export type AudioProductClass =
  | "preamp"
  | "power_amp"
  | "integrated"
  | "receiver"
  | "amplifier";

export const PREAMP_FUNCTION_BLURB =
  "This is a preamplifier: it selects and controls sources and sends a line-level signal to a power amplifier. It does not power your speakers.";

export const POWER_AMP_FUNCTION_BLURB =
  "This is a power amplifier: it takes the line-level signal from a preamp (or similar control stage) and powers your speakers.";

export const INTEGRATED_RECEIVER_FUNCTION_BLURB =
  "This powers your speakers and controls audio playback from your sources.";

export const PREAMP_LAYMAN = {
  what: "Preamplifier",
  does: "Lets you choose sources and set listening level, then sends a line-level signal to a power amplifier. It does not power the speakers itself.",
  why: "Used as the control stage in front of the power amplifier in this system.",
};

export const POWER_AMP_LAYMAN = {
  what: "Power amplifier",
  does: "Takes the line-level signal from a preamp (or similar) and powers your speakers.",
  why: "Chosen to match your speaker layout and listening level for that room.",
};

export const INTEGRATED_RECEIVER_LAYMAN = {
  what: "Integrated amplifier or AV receiver",
  does: "Powers your speakers and often switches between music or movie sources at controlled volume levels.",
  why: "Chosen to match your speaker layout and listening preferences for that room.",
};

function compactHay(parts: Array<string | null | undefined>): string {
  return parts
    .map((s) => String(s ?? "").replace(/\s+/g, " ").trim().toLowerCase())
    .filter(Boolean)
    .join(" ");
}

const PREAMP_RE = /\bpre[\s-]*amp(?:lifier)?s?\b|\bpreamplifiers?\b/;
const POWER_AMP_RE = /\bpower[\s-]*amp(?:lifier)?s?\b/;
const INTEGRATED_RE = /\bintegrated[\s-]*amp(?:lifier)?s?\b/;
const RECEIVER_RE = /\b(?:av[\s-]*receivers?|avrs?|receivers?)\b/;
/** Whole-word amp/amplifier — does not match the trailing "amp" inside "preamp". */
const GENERIC_AMP_RE = /\bamplifiers?\b|(?<![a-z])amps?\b/;

/**
 * Authoritative brand+model locks Soon can trust when category / D-Tools / snapshot is empty or wrong.
 * Production Helios rows (e.g. proposal_items 9947/859, 9948/860, 10427/896): category NULL,
 * cleoDescription NULL, descriptionSnapshot starts "The Gryphon Helios is a power amplifier…".
 * Answer-time lock must be correct before any targeted snapshot regen. Do not mass-rewrite.
 */
export const KNOWN_LINE_PREAMP_BRAND_MODELS: ReadonlyArray<{ brand: string; model: string }> = [
  { brand: "gryphon", model: "helios" },
];

function wordInHay(hay: string, word: string): boolean {
  const w = String(word ?? "").trim().toLowerCase();
  if (!w) return false;
  const escaped = w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`\\b${escaped}\\b`).test(hay);
}

export function knownBrandModelLocksAsLinePreamp(input: {
  brand?: string | null;
  model?: string | null;
  name?: string | null;
  displayName?: string | null;
}): boolean {
  const blob = [input.brand, input.model, input.name, input.displayName]
    .map((v) => String(v ?? "").toLowerCase())
    .join(" ");
  if (!blob.trim()) return false;
  return KNOWN_LINE_PREAMP_BRAND_MODELS.some(
    (entry) => wordInHay(blob, entry.brand) && wordInHay(blob, entry.model),
  );
}

export function sourceHaystackForProductClass(input: {
  category?: string | null;
  name?: string | null;
  brand?: string | null;
  model?: string | null;
  displayName?: string | null;
  rawText?: string | null;
  descriptionSnapshot?: string | null;
  catalogueDescription?: string | null;
  dtoolsOriginalDescription?: string | null;
}): string {
  return compactHay([
    input.category,
    input.name,
    input.brand,
    input.model,
    input.displayName,
    input.rawText,
    input.descriptionSnapshot,
    input.catalogueDescription,
    input.dtoolsOriginalDescription,
  ]);
}

export function detectAudioProductClass(haystack: string): AudioProductClass | null {
  const h = String(haystack ?? "").toLowerCase();
  if (!h.trim()) return null;
  // Known-product map wins even when the same haystack also says "power amplifier".
  if (
    KNOWN_LINE_PREAMP_BRAND_MODELS.some(
      (entry) => wordInHay(h, entry.brand) && wordInHay(h, entry.model),
    )
  ) {
    return "preamp";
  }
  const preamp = PREAMP_RE.test(h);
  const power = POWER_AMP_RE.test(h);
  const integrated = INTEGRATED_RE.test(h);
  const receiver = RECEIVER_RE.test(h);
  if (preamp && !integrated && !power) return "preamp";
  if (preamp && power && !integrated) return "preamp";
  if (power && !preamp) return "power_amp";
  if (integrated) return "integrated";
  if (receiver) return "receiver";
  if (GENERIC_AMP_RE.test(h) && !preamp) return "amplifier";
  if (preamp) return "preamp";
  return null;
}

/** Brand+model lock: Gryphon Helios is a line preamp regardless of empty category. */
export function lineLocksAsLinePreamp(input: {
  category?: string | null;
  name?: string | null;
  brand?: string | null;
  model?: string | null;
  displayName?: string | null;
  rawText?: string | null;
  descriptionSnapshot?: string | null;
  catalogueDescription?: string | null;
  dtoolsOriginalDescription?: string | null;
}): boolean {
  if (knownBrandModelLocksAsLinePreamp(input)) return true;
  return sourceIndicatesPreamp({
    ...input,
    // Identity class must not learn "power amp" from a wrong snapshot.
    descriptionSnapshot: copyMisidentifiesPreampAsPowerAmp(String(input.descriptionSnapshot ?? ""))
      ? ""
      : input.descriptionSnapshot,
  });
}

/** Locked preamp blurb when brand+model or category/name says preamp — snapshot cannot override. */
export function lockedPreampFunctionBlurbForLine(input: {
  category?: string | null;
  name?: string | null;
  brand?: string | null;
  model?: string | null;
  displayName?: string | null;
  rawText?: string | null;
  descriptionSnapshot?: string | null;
  catalogueDescription?: string | null;
  dtoolsOriginalDescription?: string | null;
}): string | null {
  if (lineLocksAsLinePreamp(input)) return PREAMP_FUNCTION_BLURB;
  return null;
}

export function sourceIndicatesPreamp(input: {
  category?: string | null;
  name?: string | null;
  brand?: string | null;
  model?: string | null;
  displayName?: string | null;
  rawText?: string | null;
  descriptionSnapshot?: string | null;
  catalogueDescription?: string | null;
  dtoolsOriginalDescription?: string | null;
}): boolean {
  return detectAudioProductClass(sourceHaystackForProductClass(input)) === "preamp";
}

/**
 * True when generated/stored copy claims the product itself drives speakers
 * or *is* a power amplifier — not when it merely mentions a power amp as the next stage.
 */
export function copyMisidentifiesPreampAsPowerAmp(text: string): boolean {
  const t = String(text ?? "").toLowerCase();
  if (!t.trim()) return false;
  const mentionsPreamp = PREAMP_RE.test(t);
  const asNextStage = /in front of|sends? (?:a )?line-level|line level signal|to (?:a |the )?power/.test(t);
  const deniesDrive = /\bdoes not (?:power|powers|drive|drives) (?:your |the )?(?:loud)?speakers\b/.test(t);
  if (!deniesDrive) {
    if (/powers? your speakers/.test(t)) return true;
    const powerAmpToDrive =
      /\b(?:needs |requires )?(?:a |the )?(?:separate )?power(?:[\s-]?amplifiers?|[\s-]?amps?)\s+to\s+drives?\b/.test(t);
    if (/drives? (?:your |the )?(?:loud)?speakers/.test(t) && !powerAmpToDrive) return true;
    if (/\b(?:drive|drives|driving|power|powers|powering)\b.{0,24}\b(?:your |the )?(?:loud)?speakers\b/.test(t)) {
      if (!powerAmpToDrive && !(mentionsPreamp && asNextStage)) return true;
    }
  }
  if (/\bis(?: itself)? (?:a |an )?(?:high-end |high end |premium )?power(?:[\s-]?amp)/.test(t)) return true;
  if (/\bitself is a(?:n)? (?:high-end |high end |premium )?power(?:[\s-]?amp)/.test(t)) return true;
  const mentionsPowerClass = /\bpower(?:[\s-]?amplifiers?|[\s-]?amps?)\b/.test(t);
  if (mentionsPowerClass && !mentionsPreamp && !asNextStage) return true;
  return false;
}

export function audioClassFunctionBlurb(haystack: string): string | null {
  const cls = detectAudioProductClass(haystack);
  if (cls === "preamp") return PREAMP_FUNCTION_BLURB;
  if (cls === "power_amp") return POWER_AMP_FUNCTION_BLURB;
  if (cls === "integrated" || cls === "receiver" || cls === "amplifier") {
    return INTEGRATED_RECEIVER_FUNCTION_BLURB;
  }
  return null;
}

export function audioClassLayman(haystack: string): {
  what: string;
  does: string;
  why: string;
} | null {
  const cls = detectAudioProductClass(haystack);
  if (cls === "preamp") return PREAMP_LAYMAN;
  if (cls === "power_amp") return POWER_AMP_LAYMAN;
  if (cls === "integrated" || cls === "receiver" || cls === "amplifier") {
    return INTEGRATED_RECEIVER_LAYMAN;
  }
  return null;
}

export const PRODUCT_CLASS_GROUNDING_PROMPT = [
  "Never invent product class.",
  "Prefer stored Description / Purpose (cleoDescription, customerExplanation), category, brand, and model over world knowledge.",
  "Known brand+model map: Gryphon Helios is a LINE PREAMP even when category is NULL and descriptionSnapshot calls it a power amplifier. Ignore that snapshot.",
  "If category, name, or D-Tools / catalogue text indicates preamp / pre-amplifier / preamplifier, that locked class wins even if you believe the model is a power amp.",
  "A preamplifier is a line-stage / control stage: it selects sources and sends a line-level signal to a power amplifier. It does NOT power speakers.",
  "Never use power-amp / drive-speakers language for a locked preamp.",
  "Power amplifiers, integrated amplifiers, and receivers may use drive-speakers language.",
  "If class is ambiguous, say you are not certain of the exact class or ask — never guess \"power amplifier\".",
].join(" ");
